import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react"
import { Box, CellContentPosition, TableProps, Skeleton, ConfirmationDialog } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { notUndefinedOrNull, useSelection } from "@tm/utils"
import { Note, VatRateSelectionItem } from "@tm/models"
import { useAfterPaintEffect } from "@bundles/parts/components/ListV2/helpers"
import { CustomArticleItemResponse } from "@tm/data/basket"
import { getBundleParams } from "@bundles/compilations/utils"
import { useArticleNotes } from "@bundles/compilations/hooks/useArticleNotes"
import { OwnArticleItem } from "./OwnArticleItem"
import { OwnArticlesSelection } from "./OwnArticlesSelection"
import { StyledTable } from "../../styles"
import { useListSettings } from "../useListSettings"

export type ArticleListHandle = {
    scrollToTop(): void
}

type Props = {
    articles: CustomArticleItemResponse[]
    pageIndex: number
    customPartVatRates?: VatRateSelectionItem[]
    isLoading?: boolean
    onAddArticlesToBasket(items: [CustomArticleItemResponse, number][]): void
    onEditArticle(article: CustomArticleItemResponse, note?: Note): void
    onDeleteArticles(articles: CustomArticleItemResponse[]): Promise<void>
    fetchNextPage(): void
}

export const OwnArticleList = forwardRef<ArticleListHandle, Props>((props, ref) => {
    const { articles, pageIndex, isLoading, fetchNextPage, onEditArticle, onAddArticlesToBasket, onDeleteArticles } = props
    const { translateText } = useLocalization()
    const { enableArticleNotes } = getBundleParams()
    const tableRef = useRef<HTMLDivElement>(null)
    const autoFetchIndex = useRef(0)
    const { showPurchasePrice } = useListSettings()

    useImperativeHandle(ref, () => {
        return {
            scrollToTop: () => {
                tableRef.current?.scrollTo({ top: 0 })
            },
        }
    })

    const getAllIds = useCallback(() => articles?.map((article) => article.id) || [], [articles])
    const { selectedIds, toggleSelected, selectAll, unselectAll } = useSelection(getAllIds)
    const [deleteIds, setDeleteIds] = useState<string[]>()
    const [quanities, setQuantities] = useState<Record<string, number>>({})

    const getArticleIds = useCallback(() => articles.map((x) => x.id).filter((x) => x), [articles])
    const { articleNotes } = useArticleNotes(enableArticleNotes ? getArticleIds() : [])

    const findNoteByArticleId = useCallback((articleId?: string) => articleNotes.find((note) => note.noteId === articleId), [articleNotes])

    const handleEditArticle = useCallback(
        (article: CustomArticleItemResponse) => {
            onEditArticle(article, findNoteByArticleId(article.id))
        },
        [findNoteByArticleId, onEditArticle]
    )

    const handleAddToBasket = useCallback(
        (article: CustomArticleItemResponse, quantity: number) => {
            onAddArticlesToBasket([[article, quantity]])
        },
        [onAddArticlesToBasket]
    )

    const handleToggleArticle = useCallback(
        (article: CustomArticleItemResponse) => {
            toggleSelected(article.id)
        },
        [selectedIds]
    )

    const handleDeleteArticle = useCallback((article: CustomArticleItemResponse) => {
        setDeleteIds([article.id])
    }, [])

    const handleDeleteConfirm = useCallback(async () => {
        if (deleteIds) {
            await onDeleteArticles(deleteIds.map((id) => articles.find((article) => article.id === id)).filter(notUndefinedOrNull))
            setDeleteIds(undefined)
            unselectAll()
        }
    }, [articles, deleteIds, onDeleteArticles])

    const closeDeleteDialog = useCallback(() => {
        setDeleteIds(undefined)
    }, [])

    const handleDeleteSelected = useCallback(() => {
        setDeleteIds(selectedIds)
    }, [selectedIds])

    const handleAddSelectedToBasket = useCallback(async () => {
        await onAddArticlesToBasket(
            selectedIds
                .map((id) => {
                    const article = articles.find((art) => art.id === id)
                    if (!article) {
                        return
                    }
                    const quantity = quanities[id] ?? 1
                    return [article, quantity] as [CustomArticleItemResponse, number]
                })
                .filter(notUndefinedOrNull)
        )
        unselectAll()
    }, [articles, onAddArticlesToBasket, quanities, selectedIds])

    const handleChangeQuantity = useCallback((article: CustomArticleItemResponse, quantity: number) => {
        setQuantities((prev) => ({
            ...prev,
            [article.id]: quantity,
        }))
    }, [])

    const getQuantity = useCallback(
        (article: CustomArticleItemResponse) => {
            return quanities[article.id] ?? 1
        },
        [quanities]
    )

    useAfterPaintEffect(() => {
        const scrollContainer = tableRef.current
        if (
            !!articles.length &&
            pageIndex > autoFetchIndex.current &&
            scrollContainer &&
            scrollContainer.clientHeight === scrollContainer.scrollHeight &&
            document.body.clientHeight > scrollContainer.clientHeight // prevent endless loop loading problems
        ) {
            // auto fetch until no new pageIndex comes
            autoFetchIndex.current = pageIndex
            fetchNextPage()
        }
    }, [tableRef.current, articles, pageIndex])

    const tableData: TableProps = useMemo(() => {
        const data: TableProps = {
            scrollbar: "system",
            headerStyle: "default",
            headerBackground: "inherit",
            columns: [
                { header: translateText(13177), gridSize: "1fr" }, // Article Number
                { header: translateText(25), gridSize: "2fr" }, //  Description
                { header: translateText(158), gridSize: "2fr" }, // Category
                { header: translateText(55), gridSize: "1fr", alignContent: CellContentPosition.right }, // Purchase price
                { header: translateText(1620), gridSize: "1fr", alignContent: CellContentPosition.right }, // Retail price
                { header: translateText(706), gridSize: "1fr", alignContent: CellContentPosition.right, sx: { pr: 3 } }, // Vat
                { alignContent: CellContentPosition.right }, // actions
            ],
        }
        if (!showPurchasePrice) {
            data.columns.splice(3, 1)
        }
        return data
    }, [translateText, showPurchasePrice])

    return (
        <Box overflow="hidden" flex={1} position="relative">
            <OwnArticlesSelection
                selectedIds={selectedIds}
                allSelected={articles.every((x) => selectedIds.includes(x.id))}
                onSelectAll={selectAll}
                onUnselectAll={unselectAll}
                onDeleteSelected={handleDeleteSelected}
                onAddToBasket={handleAddSelectedToBasket}
                itemsType="article"
            />
            <StyledTable {...tableData} onScrollBottom={fetchNextPage} ref={tableRef}>
                {articles.map((article) => (
                    <OwnArticleItem
                        key={article.id}
                        article={article}
                        quantity={getQuantity(article)}
                        note={findNoteByArticleId(article.id)}
                        isSelected={selectedIds.includes(article.id)}
                        isDeleting={!!deleteIds?.length}
                        onEditArticle={handleEditArticle}
                        onAddToBasket={handleAddToBasket}
                        onDeleteArticle={handleDeleteArticle}
                        onToggleArticle={handleToggleArticle}
                        onChangeQuantity={handleChangeQuantity}
                    />
                ))}
            </StyledTable>
            {isLoading && <Skeleton height={54} width="100%" />}
            <ConfirmationDialog
                open={!!deleteIds?.length}
                confirmationButtonText={translateText(585)}
                cancelButtonText={translateText(584)}
                onConfirm={handleDeleteConfirm}
                onCancel={closeDeleteDialog}
            >
                {translateText(833)}
            </ConfirmationDialog>
        </Box>
    )
})
